import merge from 'lodash/merge';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Form, IFormApi, IFormProps } from '@hotelplan/components.common.forms';
import {
  useOnSearchStateChanges,
  useSearchState,
} from '@hotelplan/fdr.lib.search.with-state';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import FiltersSkeleton from 'components/domain/filters/Filters.skeleton';
import { useFilterFormActions } from 'components/domain/filters/Filters.utils';
import useFiltersSubmitController from 'components/domain/filters/useFiltersSubmitController';
import { IFdrFiltersFormState } from 'fdr/components/candidate/fdr-search/types/filters.types';
import { IWishlistState } from './fdr-wishlist.types';

interface IWishlistFiltersFormProps extends React.PropsWithChildren<{}> {
  postSubmit?: (state: IWishlistState) => void;
  formApiRef?: React.RefObject<IFormApi<IFdrFiltersFormState>>;
}

const FiltersSubmitController: React.FC<IWishlistFiltersFormProps> = ({
  postSubmit,
  children,
}) => {
  const stateContext = useSearchState<IWishlistState>();

  useFiltersSubmitController(stateContext, postSubmit);

  return <>{children}</>;
};

const FormWrapper = styled(Form)(({ theme }) =>
  sx2CssThemeFn(
    merge({}, theme.forms.filtersForm, {
      height: ['100%', 'auto'],
      '.totalFilterCounter': { display: 'none' },
    })
  )
) as React.FC<IFormProps<IFdrFiltersFormState>>;

const FdrWishlistFiltersForm: React.FC<IWishlistFiltersFormProps> = props => {
  const { mobile } = useDeviceType();
  const defaultFormApiRef = useRef<IFormApi<IFdrFiltersFormState>>(null);
  const { postSubmit, formApiRef = defaultFormApiRef, children } = props;

  const { loading, state, setState } = useSearchState<IWishlistState>();
  const { filters, searchControl } = state || {};

  const { onSubmit, onReset } = useFilterFormActions<IWishlistState>(
    setState,
    postSubmit
  );

  const currentTravelTypeRef = useRef(searchControl?.extended_travelType?.fdr);

  useEffect(() => {
    if (
      currentTravelTypeRef.current !==
        searchControl?.extended_travelType?.fdr &&
      filters
    ) {
      formApiRef?.current?.setValues(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchControl?.extended_travelType?.fdr]);

  useOnSearchStateChanges<IWishlistState>((next, prev) => {
    if (
      next.searchControl.extended_travelType?.fdr !==
      prev.searchControl.extended_travelType?.fdr
    ) {
      formApiRef?.current?.setValues(filters);
      return;
    }

    if (next.filters) {
      formApiRef.current?.setValues(next.filters);
    }
  });

  if (loading || !filters) return <FiltersSkeleton />;

  return (
    <FormWrapper
      formApiRef={formApiRef}
      initialValues={filters}
      onSubmit={onSubmit}
      onReset={onReset}
    >
      {mobile ? (
        children
      ) : (
        <FiltersSubmitController {...props}>{children}</FiltersSubmitController>
      )}
    </FormWrapper>
  );
};

export default FdrWishlistFiltersForm;
